import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import './ProjectBlock.css';
import {benefitsData, featuresData} from "../../pages/Home/HomeBlocksData/projectsData";
import ProjectFeatureBlock from "../ProjectFeatureBlock/ProjectFeatureBlock";

const MagneticDots = ({x, y, width, height, columns, rows}) => {
    const animationFrameRef = useRef(null);
    const pointsRef = useRef([]);
    const tapTimeoutRef = useRef(null);
    const dots = Array.from({length: columns * rows}, (_, index) => ({
        x: x + (index % columns) * (width / (columns - 1)),
        y: y + Math.floor(index / columns) * (height / (rows - 1)),
    }));

    const animate = () => {
        let isMoving = false;

        pointsRef.current.forEach((point) => {
            if (!point?.node) {
                return;
            }

            point.currentX += (point.targetX - point.currentX) * .16;
            point.currentY += (point.targetY - point.currentY) * .16;

            if (Math.abs(point.targetX - point.currentX) > .05 || Math.abs(point.targetY - point.currentY) > .05) {
                isMoving = true;
            }

            point.node.setAttribute('transform', `translate(${point.currentX} ${point.currentY})`);
        });

        animationFrameRef.current = isMoving ? requestAnimationFrame(animate) : null;
    };

    const startAnimation = () => {
        if (!animationFrameRef.current) {
            animationFrameRef.current = requestAnimationFrame(animate);
        }
    };

    const handlePointerMove = (event) => {
        const svg = event.currentTarget.ownerSVGElement;
        const cursor = svg.createSVGPoint();
        const matrix = svg.getScreenCTM();

        if (!matrix) {
            return;
        }

        cursor.x = event.clientX;
        cursor.y = event.clientY;

        const position = cursor.matrixTransform(matrix.inverse());

        pointsRef.current.forEach((point) => {
            const deltaX = position.x - point.x;
            const deltaY = position.y - point.y;
            const distance = Math.hypot(deltaX, deltaY);
            const intensity = Math.max(0, 1 - distance / 70) ** 2;

            point.targetX = distance ? deltaX / distance * intensity * 12 : 0;
            point.targetY = distance ? deltaY / distance * intensity * 12 : 0;
        });

        startAnimation();
    };

    const handlePointerLeave = () => {
        pointsRef.current.forEach((point) => {
            point.targetX = 0;
            point.targetY = 0;
        });

        startAnimation();
    };

    const handlePointerDown = (event) => {
        if (event.pointerType !== 'touch') {
            return;
        }

        handlePointerMove(event);
        clearTimeout(tapTimeoutRef.current);
        tapTimeoutRef.current = setTimeout(handlePointerLeave, 500);
    };

    useEffect(() => () => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        clearTimeout(tapTimeoutRef.current);
    }, []);

    return (
        <g className='project-sales-magnetic-dots'>
            <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill='transparent'
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerLeave={handlePointerLeave}
            />
            {dots.map((dot, index) => (
                <circle
                    key={`${dot.x}-${dot.y}`}
                    cx={dot.x}
                    cy={dot.y}
                    r='1.5'
                    ref={(node) => {
                        pointsRef.current[index] = {
                            node,
                            x: dot.x,
                            y: dot.y,
                            currentX: pointsRef.current[index]?.currentX || 0,
                            currentY: pointsRef.current[index]?.currentY || 0,
                            targetX: pointsRef.current[index]?.targetX || 0,
                            targetY: pointsRef.current[index]?.targetY || 0,
                        };
                    }}
                />
            ))}
        </g>
    );
};

const ProjectBlock = ({
   sup,
   tags,
   title,
   className = '',
   isDarkTheme,
   description,
   hasFeatures,
   hasBenefits,
   hasDashboard,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const hasDetails = hasFeatures || hasBenefits || hasDashboard;

    const renderTags = tags.map((tag) => (
        <span className='project-tag'>
            {t(tag)}
        </span>
    ));

    const renderFeaturesBlocks = featuresData?.map(({ sup, title, description }) => (
        <ProjectFeatureBlock
            sup={sup}
            title={title}
            isDarkTheme={isDarkTheme}
            description={description}
        />
    ))

    const renderBenefitsBlocks = benefitsData?.map(({ sup, title, description }) => (
        <ProjectFeatureBlock
            sup={sup}
            title={title}
            isDarkTheme={isDarkTheme}
            description={description}
        />
    ))

    const renderSalesSideFeatures = featuresData
        .filter(({sup}) => [1, 3, 5].includes(sup))
        .map(({sup, title, description}) => (
            <ProjectFeatureBlock
                key={sup}
                sup={sup}
                title={title}
                isDarkTheme={isDarkTheme}
                description={description}
            />
        ));

    const renderSalesBottomFeatures = featuresData
        .filter(({sup}) => [2, 4].includes(sup))
        .map(({sup, title, description}) => (
            <ProjectFeatureBlock
                key={sup}
                sup={sup}
                title={title}
                isDarkTheme={isDarkTheme}
                description={description}
            />
        ));

    return (
        <div key={sup} className={`${className} ${hasDetails && 'project-block-expandable'} ${!isDarkTheme && 'project-block-dark'} project-block`}>
            <div className='project-wrap'>
                <div className='project-header-block'>
                    <h3 className={`project-header ${!isDarkTheme && 'project-header-dark'} `}>
                        {t(title)}
                    </h3>
                    <div className='project-tag-block'>
                        {renderTags}
                    </div>
                </div>
                {hasDetails ? (
                    <span className='project-action'>
                        <button
                            className='project-trigger'
                            type='button'
                            aria-expanded={isOpen}
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            <span className='project-toggle'>{isOpen ? t('Show less') : t('Read more')}</span>
                        </button>
                        {!isOpen && (
                            <span className={`project-descr ${!isDarkTheme && 'project-descr-dark'}`}>
                                {t(description)}
                            </span>
                        )}
                    </span>
                ) : (
                    <p className={`project-descr ${!isDarkTheme && 'project-descr-dark'}`}>
                        {t(description)}
                    </p>
                )}
            </div>
            {hasDetails && (
                <div className={`${isOpen && 'project-content-open'} project-content`}>
                    <div className='project-content-inner'>
                        {hasDashboard ? (
                            <div className='project-sales-layout'>
                                <div className='project-sales-main'>
                                    <div className='project-sales-visual'>
                                        <svg className='project-sales-performance' viewBox='0 0 700 300' role='img' aria-label='Performance overview and top teams'>
                                            <defs>
                                                <linearGradient id='team-alpha-line' gradientUnits='userSpaceOnUse' x1='487' y1='0' x2='640' y2='0'>
                                                    <stop offset='0%' stopColor='#388EFF'/><stop offset='72%' stopColor='#388EFF'/><stop offset='100%' stopColor='#388EFF' stopOpacity='0'/>
                                                </linearGradient>
                                                <linearGradient id='team-bravo-line' gradientUnits='userSpaceOnUse' x1='487' y1='0' x2='640' y2='0'>
                                                    <stop offset='0%' stopColor='#388EFF'/><stop offset='52%' stopColor='#388EFF'/><stop offset='100%' stopColor='#388EFF' stopOpacity='0'/>
                                                </linearGradient>
                                                <linearGradient id='team-charlie-line' gradientUnits='userSpaceOnUse' x1='487' y1='0' x2='640' y2='0'>
                                                    <stop offset='0%' stopColor='#388EFF'/><stop offset='32%' stopColor='#388EFF'/><stop offset='100%' stopColor='#388EFF' stopOpacity='0'/>
                                                </linearGradient>
                                            </defs>
                                            <text className='project-sales-visual-title' x='24' y='30'>{t('Performance overview')}</text>
                                            <g className='project-sales-y-axis-labels'>
                                                <text x='6' y='62'>30M</text><text x='6' y='100'>25M</text><text x='6' y='138'>20M</text><text x='6' y='176'>15M</text><text x='6' y='214'>10M</text><text x='12' y='252'>0</text>
                                            </g>
                                            <MagneticDots x={24} y={52} width={385} height={188} columns={23} rows={12}/>
                                            <path className='project-sales-chart-line' d='M44 145 L112 156 L180 156 L248 156 L328 105 L408 72'/>
                                            <g className='project-sales-chart-points'>
                                                <circle cx='44' cy='145' r='5'/><circle cx='112' cy='156' r='5'/><circle cx='180' cy='156' r='5'/><circle cx='248' cy='156' r='5'/><circle cx='328' cy='105' r='5'/><circle cx='408' cy='72' r='7'/>
                                            </g>
                                            <g className='project-sales-axis-labels'>
                                                <text x='36' y='266'>Jul</text><text x='104' y='266'>Aug</text><text x='172' y='266'>Sep</text><text x='240' y='266'>Oct</text><text x='316' y='266'>Nov</text><text x='400' y='266'>Dec</text>
                                            </g>
                                            <text className='project-sales-visual-title' x='470' y='30'>{t('Top teams')}</text>
                                            <path className='project-sales-branch' d='M408 72 C435 72 445 82 470 82'/>
                                            <path className='project-sales-branch' d='M408 72 C435 72 445 145 470 150'/>
                                            <path className='project-sales-branch' d='M408 72 C435 72 445 212 470 218'/>
                                            <g className='project-sales-team project-sales-team-first'>
                                                <circle className='project-sales-team-node-ring' cx='470' cy='82' r='17'/><circle className='project-sales-team-node' cx='470' cy='82' r='12'/><text x='470' y='82' dy='2' textAnchor='middle' dominantBaseline='middle'>1</text><text className='project-sales-team-name' x='500' y='70'>Team Alpha</text><path stroke='url(#team-alpha-line)' d='M487 82 H640'/><circle className='project-sales-team-point-mask' cx='520' cy='82' r='5'/><circle fill='url(#team-alpha-line)' cx='520' cy='82' r='4'/><circle className='project-sales-team-point-mask' cx='555' cy='82' r='5'/><circle fill='url(#team-alpha-line)' cx='555' cy='82' r='4'/><circle className='project-sales-team-point-mask' cx='590' cy='82' r='5'/><circle fill='url(#team-alpha-line)' cx='590' cy='82' r='4'/><circle className='project-sales-team-point-mask' cx='620' cy='82' r='5'/><circle fill='url(#team-alpha-line)' cx='620' cy='82' r='4'/><text className='project-sales-team-value' x='650' y='88'>8.6M</text>
                                            </g>
                                            <g className='project-sales-team'>
                                                <circle className='project-sales-team-node-ring' cx='470' cy='150' r='17'/><circle className='project-sales-team-node' cx='470' cy='150' r='12'/><text x='470' y='150' dy='2' textAnchor='middle' dominantBaseline='middle'>2</text><text className='project-sales-team-name' x='500' y='138'>Team Bravo</text><path stroke='url(#team-bravo-line)' d='M487 150 H640'/><circle className='project-sales-team-point-mask' cx='520' cy='150' r='5'/><circle fill='url(#team-bravo-line)' cx='520' cy='150' r='4'/><circle className='project-sales-team-point-mask' cx='555' cy='150' r='5'/><circle fill='url(#team-bravo-line)' cx='555' cy='150' r='4'/><circle className='project-sales-team-point-mask' cx='590' cy='150' r='5'/><circle fill='url(#team-bravo-line)' cx='590' cy='150' r='4'/><circle className='project-sales-team-point-mask' cx='620' cy='150' r='5'/><circle fill='url(#team-bravo-line)' cx='620' cy='150' r='4'/><text className='project-sales-team-value' x='650' y='156'>6.2M</text>
                                            </g>
                                            <g className='project-sales-team'>
                                                <circle className='project-sales-team-node-ring' cx='470' cy='218' r='17'/><circle className='project-sales-team-node' cx='470' cy='218' r='12'/><text x='470' y='218' dy='2' textAnchor='middle' dominantBaseline='middle'>3</text><text className='project-sales-team-name' x='500' y='206'>Team Charlie</text><path stroke='url(#team-charlie-line)' d='M487 218 H640'/><circle className='project-sales-team-point-mask' cx='520' cy='218' r='5'/><circle fill='url(#team-charlie-line)' cx='520' cy='218' r='4'/><circle className='project-sales-team-point-mask' cx='555' cy='218' r='5'/><circle fill='url(#team-charlie-line)' cx='555' cy='218' r='4'/><circle className='project-sales-team-point-mask' cx='590' cy='218' r='5'/><circle fill='url(#team-charlie-line)' cx='590' cy='218' r='4'/><circle className='project-sales-team-point-mask' cx='620' cy='218' r='5'/><circle fill='url(#team-charlie-line)' cx='620' cy='218' r='4'/><text className='project-sales-team-value' x='650' y='224'>4.7M</text>
                                            </g>
                                        </svg>
                                        <svg className='project-sales-calendar' viewBox='0 0 700 145' role='img' aria-label='Incentive calendar'>
                                            <text className='project-sales-visual-title' x='24' y='28'>{t('Incentive calendar')}</text>
                                            <g className='project-sales-calendar-labels'>
                                                <text x='36' y='52'>Jan</text><text x='78' y='52'>Feb</text><text x='130' y='52'>Mar</text><text x='184' y='52'>Apr</text><text x='230' y='52'>May</text><text x='294' y='52'>Jun</text><text x='350' y='52'>Jul</text><text x='410' y='52'>Aug</text><text x='470' y='52'>Sep</text><text x='530' y='52'>Oct</text><text x='590' y='52'>Nov</text><text x='648' y='52'>Dec</text>
                                            </g>
                                            <MagneticDots x={24} y={42} width={652} height={76} columns={37} rows={5}/>
                                            <path className='project-sales-calendar-line' d='M42 78 C57 78 73 72 88 72 C105 72 123 66 140 66 C158 66 176 74 194 74 C209 74 225 64 240 64 C261 64 283 68 304 68 C323 68 341 78 360 78 C380 78 400 74 420 74 C440 74 460 68 480 68 C500 68 520 65 540 65 C560 65 580 72 600 72 C620 72 640 62 660 62'/>
                                            <g className='project-sales-calendar-points'>
                                                <circle cx='42' cy='78' r='4'/><circle cx='88' cy='72' r='4'/><circle className='project-sales-calendar-event-ring' cx='140' cy='66' r='8'/><circle className='project-sales-calendar-event-core' cx='140' cy='66' r='5'/><circle cx='194' cy='74' r='4'/><circle cx='240' cy='64' r='4'/><circle className='project-sales-calendar-event-ring' cx='304' cy='68' r='8'/><circle className='project-sales-calendar-event-core' cx='304' cy='68' r='5'/><circle cx='360' cy='78' r='4'/><circle cx='420' cy='74' r='4'/><circle cx='480' cy='68' r='4'/><circle cx='540' cy='65' r='4'/><circle cx='600' cy='72' r='4'/><circle className='project-sales-calendar-event-ring' cx='660' cy='62' r='8'/><circle className='project-sales-calendar-event-core' cx='660' cy='62' r='5'/>
                                            </g>
                                            <g className='project-sales-raffle'>
                                                <path d='M140 74 V102'/><circle cx='140' cy='112' r='14'/><text x='140' y='112' dy='3' fontSize='20' textAnchor='middle' dominantBaseline='middle'>★</text><text className='project-sales-raffle-label' x='140' y='136' textAnchor='middle'>{t('Q1 Raffle')}</text>
                                                <path d='M304 76 V102'/><circle cx='304' cy='112' r='14'/><text x='304' y='112' dy='3' fontSize='20' textAnchor='middle' dominantBaseline='middle'>★</text><text className='project-sales-raffle-label' x='304' y='136' textAnchor='middle'>{t('Mid-Year Raffle')}</text>
                                                <path d='M660 70 V102'/><circle cx='660' cy='112' r='14'/><text x='660' y='112' dy='3' fontSize='20' textAnchor='middle' dominantBaseline='middle'>★</text><text className='project-sales-raffle-label' x='660' y='136' textAnchor='middle'>{t('Year-End Raffle')}</text>
                                            </g>
                                        </svg>
                                    </div>
                                    <div className='project-sales-side'>
                                        {renderSalesSideFeatures}
                                    </div>
                                </div>
                                <div className='project-sales-bottom'>
                                    {renderSalesBottomFeatures}
                                </div>
                            </div>
                        ) : hasFeatures && (
                            <div className='project-sub-wrap'>
                                <h4 className={`${!isDarkTheme && 'project-sub-head-dark'} project-sub-head`}>{t('Key Features')}:</h4>
                                <div className='project-sub-descr'>
                                    {renderFeaturesBlocks}
                                </div>
                            </div>
                        )}
                        {hasBenefits && (
                            <div>
                                <h4 className={`${!isDarkTheme && 'project-sub-head-dark'} project-sub-head`}>{t('Benefits')}:</h4>
                                <div className='project-sub-descr'>
                                    {renderBenefitsBlocks}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>

    );
};

export default ProjectBlock;
